import { InputLabel, Switch, Box, TextField, FormControl, Select, MenuItem, Divider, Card, CardHeader, CardContent, FormControlLabel, FormHelperText, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from "src/contexts/UserContext";
import { SBT } from 'src/contexts/SBT';
import { fetchAuthSession } from 'aws-amplify/auth';

export default function MessagingOptions(props) {
	const {
		formikProps,
		availableMessageTemplates,
		emailMessagingSkills,
		smsMessagingSkills,
		smsPhoneNumbers,
		ivrScripts,
	} = props;
	const userContext = useContext(UserContext);

	const [sbtTemplates, setSbtTemplates] = useState([]);

	useEffect(() => {
		async function getTemplates() {
			const token = (await fetchAuthSession()).tokens.idToken;
			const apiKey = userContext.apiKey;
			const templates = await SBT.getTemplates(
				token,
				apiKey,
				formikProps.values.channels.sms.isProd,
				formikProps.values.channels.sms.clientId,
				formikProps.values.channels.sms.clientSecret,
				formikProps.values.channels.sms.groupId
			)
			setSbtTemplates(templates);
		}
		if (formikProps.values.channels.sms.clientId && formikProps.values.channels.sms.clientSecret && formikProps.values.channels.sms.groupId) {
			getTemplates();
		}
	}, [formikProps.values.channels.sms.clientId, 
		formikProps.values.channels.sms.clientSecret, 
		formikProps.values.channels.sms.groupId]);

	return (
		<Card variant="outlined">
			<CardHeader
				title="Messaging Options"
				titleTypographyProps={{
					variant: 'body2',
					color: 'textSecondary',
				}}
				subheader={
					userContext.telephonyProvider === 'Webex' ? (
						<Typography
							variant="body"
							color="textSecondary"
							style={{ padding: '6px' }}
						>
							<span style={{ color: 'yellow' }}>⚠</span> In order to send an
							email with Webex, you must set up an email app in Webex Connect
							beforehand.
						</Typography>
					) : null
				}
			/>
			<CardContent>
				<Card elevation={0}>
					<CardHeader
						title={
							<FormControlLabel
								control={
									<Switch
										checked={formikProps.values.channels.emailEnabled}
										onChange={formikProps.handleChange}
										name="channels.emailEnabled"
										color="primary"
									/>
								}
								label="Email"
							/>
						}
					/>
					{formikProps.values.channels.emailEnabled && (
						<CardContent>
							<Box
								display="grid"
								gridTemplateColumns="1fr 1fr"
								alignItems="center"
								gap={2}
							>
								{/* Email provider */}
								<FormControl>
									<InputLabel>Email Provider</InputLabel>
									<Select
										name="channels.email.providerName"
										label="Email Provider"
										value={formikProps.values.channels.email.providerName}
										onBlur={formikProps.handleBlur}
										onChange={formikProps.handleChange}
									>
										{userContext.telephonyProvider === 'Genesys' && (
											<MenuItem key="genesys" value="genesys">
												Genesys
											</MenuItem>
										)}
										{userContext.telephonyProvider === 'NiC' && (
											<MenuItem key="incontact" value="incontact">
												InContact
											</MenuItem>
										)}
										{userContext.telephonyProvider === 'Webex' && (
											<MenuItem key="webex" value="webex">
												Webex
											</MenuItem>
										)}
										<MenuItem key="ses" value="ses">
											Amazon SES
										</MenuItem>
									</Select>
								</FormControl>
								{/* Message template */}
								<FormControl>
									<InputLabel
										error={
											formikProps.errors.channels?.email?.messageTemplate &&
											formikProps.touched?.channels?.email?.messageTemplate
										}
									>
										Message Template
									</InputLabel>
									<Select
										name="channels.email.messageTemplate"
										label="Message Template"
										value={formikProps.values.channels.email.messageTemplate}
										onBlur={formikProps.handleBlur}
										onChange={formikProps.handleChange}
										error={
											formikProps.errors.channels?.email?.messageTemplate &&
											formikProps.touched?.channels?.email?.messageTemplate
										}
									>
										{availableMessageTemplates.map((template) => (
											<MenuItem key={template.id} value={template.id}>
												{template.name}
											</MenuItem>
										))}
									</Select>
									{formikProps.touched.channels?.email?.messageTemplate &&
										formikProps.errors.channels?.email?.messageTemplate && (
											<FormHelperText error>
												{formikProps.errors.channels?.email?.messageTemplate}
											</FormHelperText>
										)}
								</FormControl>

								{/* Email subject */}
								<TextField
									margin="dense"
									name="channels.email.subject"
									label="Subject"
									type="text"
									value={formikProps.values.channels.email.subject}
									onChange={formikProps.handleChange}
									onBlur={formikProps.handleBlur}
									error={
										formikProps.errors.channels?.email?.subject &&
										formikProps.touched?.channels?.email?.subject
									}
									helperText={
										formikProps.touched.channels?.email?.subject &&
										formikProps.errors.channels?.email?.subject
									}
								/>
								{/* Sending email */}
								<TextField
									margin="dense"
									name="channels.email.sendingEmail"
									label="Sending Email"
									type="text"
									value={formikProps.values.channels.email.sendingEmail}
									onChange={formikProps.handleChange}
									onBlur={formikProps.handleBlur}
									error={
										formikProps.errors.channels?.email?.sendingEmail &&
										formikProps.touched?.channels?.email?.sendingEmail
									}
									helperText={
										formikProps.touched.channels?.email?.sendingEmail &&
										formikProps.errors.channels?.email?.sendingEmail
									}
								/>
								{/* Sending email display name */}
								{userContext.telephonyProvider !== 'NiC' && (
									<TextField
										margin="dense"
										name="channels.email.sendingEmailDisplayName"
										label="Sending Email Display Name"
										placeholder="Enter a display name"
										type="text"
										value={
											formikProps.values.channels.email.sendingEmailDisplayName
										}
										onChange={formikProps.handleChange}
										onBlur={formikProps.handleBlur}
										error={
											formikProps.errors.channels?.email
												?.sendingEmailDisplayName &&
											formikProps.touched?.channels?.email
												?.sendingEmailDisplayName
										}
										helperText="Optional. If not provided, the sending email will be used."
									/>
								)}
								{/* Email skill */}
								{formikProps.values.channels.email.providerName ===
									'incontact' && (
									<FormControl>
										<InputLabel
											error={
												formikProps.errors.channels?.email?.emailSkill &&
												formikProps.touched?.channels?.email?.emailSkill
											}
										>
											Email Skill
										</InputLabel>
										<Select
											margin="dense"
											name="channels.email.emailSkill"
											label="Email Skill"
											value={formikProps.values.channels.email.emailSkill}
											onChange={formikProps.handleChange}
											onBlur={formikProps.handleBlur}
											error={
												formikProps.errors.channels?.email?.emailSkill &&
												formikProps.touched?.channels?.email?.emailSkill
											}
										>
											{emailMessagingSkills.map((skill) => (
												<MenuItem key={skill.skillId} value={skill.skillId}>
													{skill.skillName}
												</MenuItem>
											))}
										</Select>
										{formikProps.touched.channels?.email?.emailSkill &&
											formikProps.errors.channels?.email?.emailSkill && (
												<FormHelperText error>
													{formikProps.errors.channels?.email?.emailSkill}
												</FormHelperText>
											)}
									</FormControl>
								)}
								{/* Email IVR script */}
								{formikProps.values.channels.email.providerName ===
									'incontact' && (
									<FormControl>
										<InputLabel
											error={
												formikProps.errors.channels?.email?.emailIVRScript &&
												formikProps.touched?.channels?.email?.emailIVRScript
											}
										>
											Email IVR Script
										</InputLabel>
										<Select
											margin="dense"
											name="channels.email.emailIVRScript"
											label="Email IVR Script"
											value={formikProps.values.channels.email.emailIVRScript}
											onChange={formikProps.handleChange}
											onBlur={formikProps.handleBlur}
											error={
												formikProps.errors.channels?.email?.emailIVRScript &&
												formikProps.touched?.channels?.email?.emailIVRScript
											}
										>
											{ivrScripts.map((script) => (
												<MenuItem
													key={script.scriptId}
													value={script.scriptName}
												>
													{script.scriptName}
												</MenuItem>
											))}
										</Select>
										{formikProps.touched.channels?.email?.emailIVRScript &&
											formikProps.errors.channels?.email?.emailIVRScript && (
												<FormHelperText error>
													{formikProps.errors.channels?.email?.emailIVRScript}
												</FormHelperText>
											)}
									</FormControl>
								)}
								{/* SES Fields */}
								{formikProps.values.channels.email.providerName === 'ses' && (
									<>
										<FormControl>
											<InputLabel
												error={
													formikProps.errors.channels?.email?.awsRegion &&
													formikProps.touched?.channels?.email?.awsRegion
												}
											>
												AWS Region
											</InputLabel>
											<Select
												name="channels.email.awsRegion"
												label="AWS Region"
												value={formikProps.values.channels.email.awsRegion}
												onBlur={formikProps.handleBlur}
												onChange={formikProps.handleChange}
												error={
													formikProps.errors.channels?.email?.awsRegion &&
													formikProps.touched?.channels?.email?.awsRegion
												}
											>
												<MenuItem key="us-east-1" value="us-east-1">
													us-east-1
												</MenuItem>
												<MenuItem key="us-east-2" value="us-east-2">
													us-east-2
												</MenuItem>
												<MenuItem key="us-west-1" value="us-west-1">
													us-west-1
												</MenuItem>
												<MenuItem key="us-west-2" value="us-west-2">
													us-west-2
												</MenuItem>
											</Select>
										</FormControl>
										<TextField
											margin="dense"
											name="channels.email.awsAccessKey"
											label="AWS Access Key"
											type="text"
											value={formikProps.values.channels.email.awsAccessKey}
											onChange={formikProps.handleChange}
											onBlur={formikProps.handleBlur}
											error={
												formikProps.errors.channels?.email?.awsAccessKey &&
												formikProps.touched?.channels?.email?.awsAccessKey
											}
											helperText={
												formikProps.touched.channels?.email?.awsAccessKey &&
												formikProps.errors.channels?.email?.awsAccessKey
											}
										/>
										<TextField
											margin="dense"
											name="channels.email.awsSecretKey"
											label="AWS Secret Key"
											type="password"
											value={formikProps.values.channels.email.awsSecretKey}
											onChange={formikProps.handleChange}
											onBlur={formikProps.handleBlur}
											error={
												formikProps.errors.channels?.email?.awsSecretKey &&
												formikProps.touched?.channels?.email?.awsSecretKey
											}
											helperText={
												formikProps.touched.channels?.email?.awsSecretKey &&
												formikProps.errors.channels?.email?.awsSecretKey
											}
										/>
									</>
								)}
							</Box>
						</CardContent>
					)}
				</Card>
				<Divider />
				<Card elevation={0}>
					<CardHeader
						title={
							<FormControlLabel
								control={
									<Switch
										checked={formikProps.values.channels.smsEnabled}
										onChange={formikProps.handleChange}
										name="channels.smsEnabled"
										color="primary"
									/>
								}
								label="SMS"
							/>
						}
					/>
					{formikProps.values.channels.smsEnabled && 
						<CardContent>
							<Box
								display="grid"
								gridTemplateColumns="1fr 1fr"
								gap={2}
								alignItems="center"
							>
								{/* SMS provider */}
								<FormControl>
									<InputLabel>SMS Provider</InputLabel>
									<Select
										name="channels.sms.providerName"
										label="SMS Provider"
										value={formikProps.values.channels.sms.providerName}
										onBlur={formikProps.handleBlur}
										onChange={formikProps.handleChange}
									>
										{userContext.telephonyProvider === 'Genesys' && (
											<MenuItem key="genesys" value="genesys">
												Genesys
											</MenuItem>
										)}
										{userContext.telephonyProvider === 'NiC' && (
											<MenuItem key="incontact" value="incontact">
												InContact
											</MenuItem>
										)}
										{userContext.telephonyProvider === 'Webex' && (
											<MenuItem key="webex" value="webex">
												Webex
											</MenuItem>
										)}
										{userContext.telephonyProvider === 'RingCX' &&
										<MenuItem key='ringCx' value='ringCx'>RingCx</MenuItem>
										}
										<MenuItem key="textel" value="textel">
											Textel
										</MenuItem>
										<MenuItem key='sbt' value='sbt'>
											Solutions By Text
										</MenuItem>
										{/* <MenuItem key='ses' value='ses'>Amazon SES</MenuItem> */}
									</Select>
								</FormControl>
								{/* Message template */}
								<FormControl>
									<InputLabel
										error={
											formikProps.errors.channels?.sms?.messageTemplate &&
											formikProps.touched?.channels?.sms?.messageTemplate
										}
									>
										Message Template
									</InputLabel>
									<Select
										name="channels.sms.messageTemplate"
										label="Message Template"
										value={formikProps.values.channels.sms.messageTemplate}
										onBlur={formikProps.handleBlur}
										onChange={formikProps.handleChange}
										error={
											formikProps.errors.channels?.sms?.messageTemplate &&
											formikProps.touched?.channels?.sms?.messageTemplate
										}
									>
										{availableMessageTemplates
											.filter((filter) => filter.type === 'PlainText')
											.map((template) => (
												<MenuItem key={template.id} value={template.id}>
													{template.name}
												</MenuItem>
											))}
									</Select>
									{formikProps.touched.channels?.sms?.messageTemplate &&
										formikProps.errors.channels?.sms?.messageTemplate && (
											<FormHelperText error>
												{formikProps.errors.channels?.sms?.messageTemplate}
											</FormHelperText>
										)}
								</FormControl>
								{/* SMS skill */}
								{formikProps.values.channels.sms.providerName ===
									'incontact' && (
									<FormControl>
										<InputLabel
											error={
												formikProps.errors.channels?.sms?.smsSkill &&
												formikProps.touched?.channels?.sms?.smsSkill
											}
										>
											SMS Skill
										</InputLabel>
										<Select
											margin="dense"
											name="channels.sms.smsSkill"
											value={formikProps.values.channels.sms.smsSkill}
											onChange={formikProps.handleChange}
											error={
												formikProps.errors.channels?.sms?.smsSkill &&
												formikProps.touched?.channels?.sms?.smsSkill
											}
										>
											{smsMessagingSkills.map((skill) => (
												<MenuItem key={skill.skillId} value={skill.skillId}>
													{skill.skillName}
												</MenuItem>
											))}
										</Select>
										{formikProps.touched.channels?.sms?.smsSkill &&
											formikProps.errors.channels?.sms?.smsSkill && (
												<FormHelperText error>
													{formikProps.errors.channels?.sms?.smsSkill}
												</FormHelperText>
											)}
									</FormControl>
								)}
								{/* SMS IVR script */}
								{formikProps.values.channels.sms.providerName ===
									'incontact' && (
									<FormControl>
										<InputLabel
											error={
												formikProps.errors.channels?.sms?.smsSkill &&
												formikProps.touched?.channels?.sms?.smsSkill
											}
										>
											SMS IVR Script
										</InputLabel>
										<Select
											margin="dense"
											name="channels.sms.smsIVRScript"
											value={formikProps.values.channels.sms.smsIVRScript}
											onChange={formikProps.handleChange}
											error={
												formikProps.errors.channels?.sms?.smsIVRScript &&
												formikProps.touched?.channels?.sms?.smsIVRScript
											}
										>
											{ivrScripts.map((script) => (
												<MenuItem
													key={script.scriptId}
													value={script.scriptName}
												>
													{script.scriptName}
												</MenuItem>
											))}
										</Select>
										{formikProps.touched.channels?.sms?.smsIVRScript &&
											formikProps.errors.channels?.sms?.smsIVRScript && (
												<FormHelperText error>
													{formikProps.errors.channels?.sms?.smsIVRScript}
												</FormHelperText>
											)}
									</FormControl>
								)}
								{/* SMS sending number */}
								{formikProps.values.channels.sms.providerName === 'genesys' && (
									<FormControl>
										<InputLabel
											error={
												formikProps.errors.channels?.sms?.sendingNumber &&
												formikProps.touched?.channels?.sms?.sendingNumber
											}
										>
											Sending SMS Number
										</InputLabel>
										<Select
											margin="dense"
											name="channels.sms.sendingNumber"
											value={formikProps.values.channels.sms.sendingNumber}
											onChange={formikProps.handleChange}
											error={
												formikProps.errors.channels?.sms?.sendingNumber &&
												formikProps.touched?.channels?.sms?.sendingNumber
											}
										>
											{smsPhoneNumbers.length === 0 && (
												<MenuItem key="none" value="none" disabled>
													No SMS numbers available
												</MenuItem>
											)}
											{smsPhoneNumbers.map((phoneNumber) => (
												<MenuItem key={phoneNumber} value={phoneNumber}>
													{phoneNumber}
												</MenuItem>
											))}
										</Select>
										{formikProps.touched.channels?.sms?.smsSkill &&
											formikProps.errors.channels?.sms?.smsSkill && (
												<FormHelperText error>
													{formikProps.errors.channels?.sms?.smsSkill}
												</FormHelperText>
											)}
									</FormControl>
								)}
								{(formikProps.values.channels.sms.providerName === 'textel' 
									|| formikProps.values.channels.sms.providerName === 'webex'
									|| formikProps.values.channels.sms.providerName === 'ringCx') && 
									<TextField
										margin="dense"
										name="channels.sms.sendingNumber"
										label="Sending SMS"
										type="text"
										value={formikProps.values.channels.sms.sendingNumber}
										onChange={formikProps.handleChange}
										onBlur={formikProps.handleBlur}
										helperText={
											formikProps.values.channels.sms.providerName === 'webex'
												? 'Enter your Connect SMS number exactly as it appears in Webex Connect'
												: ''
										}
									/>
								}
								{/* Textel bearer token */}
								{formikProps.values.channels.sms.providerName === 'textel' && (
									<TextField
										margin="dense"
										name="channels.sms.bearerToken"
										label="Textel Bearer Token"
										type="text"
										value={formikProps.values.channels.sms.bearerToken}
										onChange={formikProps.handleChange}
										onBlur={formikProps.handleBlur}
									/>
								)}
								{/* SES Fields */}
								{/* {formikProps.values.channels.sms.providerName === 'ses' &&
								<>
									<FormControl>
										<InputLabel error={formikProps.errors.channels?.sms?.awsRegion && formikProps.touched?.channels?.sms?.awsRegion}>AWS Region</InputLabel>
										<Select
											name="channels.sms.awsRegion"
											label="AWS Region"
											value={formikProps.values.channels.sms.awsRegion}
											onBlur={formikProps.handleBlur}
											onChange={formikProps.handleChange}
											error={formikProps.errors.channels?.sms?.awsRegion && formikProps.touched?.channels?.sms?.awsRegion}
										>
											<MenuItem key='us-east-1'
												value='us-east-1'>us-east-1</MenuItem>
											<MenuItem key='us-east-2'
												value='us-east-2'>us-east-2</MenuItem>
											<MenuItem key='us-west-1'
												value='us-west-1'>us-west-1</MenuItem>
											<MenuItem key='us-west-2'
												value='us-west-2'>us-west-2</MenuItem>
										</Select>
									</FormControl>
									<TextField
										margin="dense"
										name="channels.sms.awsAccessKey"
										label="AWS Access Key"
										type="password"
										value={formikProps.values.channels.sms.awsAccessKey}
										onChange={formikProps.handleChange}
										onBlur={formikProps.handleBlur}
										error={formikProps.errors.channels?.sms?.awsAccessKey && formikProps.touched?.channels?.sms?.awsAccessKey}
										helperText={formikProps.touched.channels?.sms?.awsAccessKey && formikProps.errors.channels?.sms?.awsAccessKey}
									/>
									<TextField
										margin="dense"
										name="channels.sms.awsSecretKey"
										label="AWS Secret Key"
										type="text"
										value={formikProps.values.channels.sms.awsSecretKey}
										onChange={formikProps.handleChange}
										onBlur={formikProps.handleBlur}
										error={formikProps.errors.channels?.sms?.awsSecretKey && formikProps.touched?.channels?.sms?.awsSecretKey}
										helperText={formikProps.touched.channels?.sms?.awsSecretKey && formikProps.errors.channels?.sms?.awsSecretKey}
									/>
								</>
							} */}
							{formikProps.values.channels.sms.providerName === 'sbt' &&
								<>
									<FormControlLabel sx={{gridColumn: 'span 2'}} control={<Switch checked={formikProps.values.channels.sms.isProd} onChange={formikProps.handleChange} name="channels.sms.isProd" color="primary" />} label="SBT Prod" />
									<TextField
										margin="dense"
										name="channels.sms.clientId"
										label="SBT Client ID"
										type="text"

										value={formikProps.values.channels.sms.clientId}
										onChange={formikProps.handleChange}
										onBlur={formikProps.handleBlur}
									/>
									<TextField
										margin="dense"
										name="channels.sms.clientSecret"
										label="SBT Client Secret"
										type="text"

										value={formikProps.values.channels.sms.clientSecret}
										onChange={formikProps.handleChange}
										onBlur={formikProps.handleBlur}
									/>
									<TextField
										margin="dense"
										name="channels.sms.groupId"
										label="SBT Group ID"
										type="text"

										value={formikProps.values.channels.sms.groupId}
										onChange={formikProps.handleChange}
										onBlur={formikProps.handleBlur}
									/>
									
									<FormControl>
										<InputLabel error={formikProps.errors.channels?.sms?.templateId && formikProps.touched?.channels?.sms?.templateId}>SBT Template</InputLabel>
										<Select
											margin="dense"
											name="channels.sms.templateId"
											value={formikProps.values.channels.sms.templateId}

											onChange={formikProps.handleChange}
											error={formikProps.errors.channels?.sms?.templateId && formikProps.touched?.channels?.sms?.templateId}
										>
											{sbtTemplates.length === 0 &&
												<MenuItem key='none' value='none' disabled>No templates available</MenuItem>
											}
											{sbtTemplates.map((template) =>
												<MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>)}
										</Select>
										{formikProps.touched.channels?.sms?.templateId && formikProps.errors.channels?.sms?.templateId &&
											<FormHelperText error>{formikProps.errors.channels?.sms?.templateId}</FormHelperText>}
									</FormControl>
								</>
							}
						</Box>
					</CardContent>}
				</Card>
			</CardContent>
		</Card>
	);
}
